import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create a single supabase client for interacting with your database
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'skillmatch'
    }
  },
  db: {
    schema: 'public'
  },
  // Add retryable HTTP client config
  httpClient: {
    retry: {
      count: 3,
      delay: 1000
    }
  }
});

// Improved retry helper with exponential backoff
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000,
  maxDelay = 10000
): Promise<T> => {
  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's a 4xx error (except 408 Request Timeout and 429 Too Many Requests)
      if (error?.status >= 400 && error?.status < 500) {
        if (error?.status !== 408 && error?.status !== 429) {
          throw error;
        }
      }

      if (attempt < maxRetries - 1) {
        // Exponential backoff with jitter
        const jitter = Math.random() * 200;
        await new Promise(resolve => 
          setTimeout(resolve, Math.min(delay + jitter, maxDelay))
        );
        delay *= 2; // Exponential increase
      }
    }
  }
  
  throw lastError;
};

// Improved connection check with retry
export const checkSupabaseConnection = async () => {
  try {
    const result = await retryOperation(async () => {
      const { error } = await supabase
        .from('resumes')
        .select('count', { count: 'exact', head: true })
        .limit(1);
        
      if (error) throw error;
      return true;
    });
    
    return result;
  } catch (error) {
    console.error('Supabase connection error:', error);
    return false;
  }
};

// Helper to get signed URL with retry
export const getSignedUrl = async (bucket: string, path: string): Promise<string> => {
  const { data, error } = await retryOperation(() => 
    supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600)
  );

  if (error) throw error;
  return data.signedUrl;
};